import { useState, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { SocketContext } from "../../context/socket";
import useStandingsHandler from "../../hooks/useStandingsHandler";
import StandingsSelect from "../headtohead/StandingsSelect";

export default function TeamsGrid({
   loadDownloader,
   clearDownloader
}) {
   const socket = useContext(SocketContext);
   const [allTeams, setAllTeams] = useState([]);
   const [selectedRound, setSelectedRound] = useState(0);
   const [selectedArchetype, setSelectedArchetype] = useState(0); // 0=None, 1=Archetype, 2=Team
   const [selectedRows, setSelectedRows] = useState(8);
   const [selectedColumns, setSelectedColumns] = useState(1);

   const [
      standings,
      setStandings,
      cachedStandings,
      tournamentRounds,
      notificationStandings,
      {
         getCachedStandings,
      },
   ] = useStandingsHandler();

   useEffect(() => {
      getCachedStandings();
      socket.emit("getAllCustomLeaderboard", (response) => {
         if (response) {
            // Filter for Type 1 (teams)
            const teams = response.filter((item) => item.Type === 1);
            setAllTeams(teams);
         }
      });
   }, []);

   const handleStandingsChange = (data) => {
      if (data && data.id !== null && data.id !== undefined) {
         // Round 0 (Alphabetical) is value 9999999
         const roundId = data.id === '0' ? 0 : (data.id === '9999999' ? 9999999 : Number(data.id));
         setSelectedRound(roundId);
      }
   };

   const handleArchetypeChange = (e) => {
      setSelectedArchetype(e.target.value);
   };

   const handleRowsChange = (e) => {
      const value = parseInt(e.target.value) || 8;
      setSelectedRows(Math.max(1, Math.min(32, value)));
   };

   const handleColumnsChange = (e) => {
      const value = parseInt(e.target.value) || 1;
      const newColumns = Math.max(1, Math.min(4, value));
      setSelectedColumns(newColumns);
      // If columns is 2, automatically set archetype to 0 (None)
      if (newColumns === 2) {
         setSelectedArchetype(0);
      }
   };

   let columns;
   let initialState;

   columns = [
      {
         field: "id",
         headerName: "ID",
         editable: false,
         width: 100,
         filterable: false,
      },
      {
         field: "Title",
         headerName: "Team Name",
         editable: false,
         width: 400,
      },
      {
         field: "LoadTeam",
         headerName: "",
         editable: false,
         width: 200,
         filterable: false,
         sortable: false,
         renderCell: (params) => (
            <>
               <Button
                  size='small'
                  onClick={() => {
                     // Create params object with all settings
                     const teamParams = {
                        teamId: params.row.id,
                        roundId: selectedRound === 0 || selectedRound === '0' ? 0 : Number(selectedRound),
                        archetype: selectedColumns === 2 ? 0 : selectedArchetype,
                        columns: selectedColumns,
                        rows: selectedRows
                     };
                     loadDownloader("team", params.row.id, JSON.stringify(teamParams), "Team_" + params.row.Title.replace(/[^a-zA-Z0-9]/g, ""));
                  }}
                  sx={{ mr: 1 }}
               >
                  LOAD
               </Button>
            </>
         ),
      }
   ];

   initialState = {
      sorting: {
         sortModel: [{ field: "Title", sort: "asc" }],
      },
      columns: {
         columnVisibilityModel: {
            id: false
         },
      },
   };

   const refreshGrid = () => {
      getCachedStandings();
      socket.emit("getAllCustomLeaderboard", (response) => {
         if (response) {
            const teams = response.filter((item) => item.Type === 1);
            setAllTeams(teams);
         }
      });
      clearDownloader();
   };

   return (
      <>
         <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h3" component="div" sx={{ textTransform: "uppercase" }}>Teams</Typography>
            <Button onClick={refreshGrid} color="error" variant="outlined">Refresh Data</Button>
         </Stack>
         
         <Box sx={{ py: 2, px: 4, backgroundColor: "#fff", mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
               <StandingsSelect 
                  selected={selectedRound}
                  callback={handleStandingsChange}
                  backgroundColor={null}
               />

               {selectedColumns !== 2 && (
                  <FormControl sx={{ minWidth: 200 }}>
                     <InputLabel id="archetype-select-label">Display</InputLabel>
                     <Select
                        labelId="archetype-select-label"
                        value={selectedArchetype}
                        label="Display"
                        onChange={handleArchetypeChange}
                     >
                        <MenuItem value={0}>None</MenuItem>
                        <MenuItem value={1}>Archetype</MenuItem>
                        <MenuItem value={2}>Team</MenuItem>
                     </Select>
                  </FormControl>
               )}

               <TextField
                  label="Rows"
                  type="number"
                  value={selectedRows}
                  onChange={handleRowsChange}
                  inputProps={{ min: 1, max: 32 }}
                  sx={{ width: 120 }}
               />

               <TextField
                  label="Columns"
                  type="number"
                  value={selectedColumns}
                  onChange={handleColumnsChange}
                  inputProps={{ min: 1, max: 4 }}
                  sx={{ width: 120 }}
               />
            </Stack>
         </Box>
         
         <Box height={400} width="100%">
            {allTeams && (
               <DataGrid
                  initialState={initialState}
                  sx={{ backgroundColor: "#efefef" }}
                  rows={allTeams}
                  rowHeight={40}
                  columns={columns}
                  rowsPerPageOptions={[100, 200, 300, 1000, 5000]}
                  components={{ Toolbar: GridToolbar }}
                  componentsProps={{
                     toolbar: {
                        printOptions: {
                           disableToolbarButton: true,
                        },
                     },
                  }}
                  disableColumnSelector
                  disableDensitySelector
                  disableSelectionOnClick
               />
            )}
         </Box>
      </>
   );
}
